import { createRef, useEffect } from 'react';
import { Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ContentSection } from 'components/Elements/Layout/ContentSection';
import { PaymentItem } from 'components/Modules/DataDisplay/PhysicianSearchGrid';
import { PageWrapper } from 'components/Modules/Wrappers/PageWrapper';
import { appConfig } from 'config/appConfig';

export interface ProviderPaymentLocationState {
  token: string;
  details: Array<Pick<PaymentItem, 'license' | 'firstName' | 'lastName' | 'amount'>>;
}

export interface ProviderPaymentProps {
  /** Same-origin HPP document for Storybook. Skips the Authorize.net form POST when set. */
  hppSrc?: string;
}

export const ProviderPayment: React.FC<ProviderPaymentProps> = ({ hppSrc }) => {
  const location = useLocation();
  const state = location.state as ProviderPaymentLocationState | null;
  const src = appConfig.hppUrl;
  const formRef = createRef<HTMLFormElement>();

  useEffect(() => {
    if (hppSrc) return;
    formRef.current?.submit();
  }, [formRef, hppSrc]);

  const details = state?.details ?? [];

  return (
    <PageWrapper sx={{ paddingX: 0 }}>
      <Stack spacing={2} p={3} alignItems="center">
        <ContentSection elevation={1} title={'Provider Assessment Details'} sx={{ width: '100%', maxWidth: 900 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              padding: 1,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              backgroundColor: 'grey.100',
            }}
          >
            <Typography flex={1} fontWeight={700} color="primary.main">
              License Number
            </Typography>
            <Typography flex={1} fontWeight={700} color="primary.main">
              Name
            </Typography>
            <Typography flex={1} textAlign="right" fontWeight={700} color="primary.main">
              Amount
            </Typography>
          </Stack>
          {details.map((x, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              sx={{
                padding: 1,
                borderBottom: '1px solid',
                borderColor: 'grey.300',
                backgroundColor: i % 2 === 1 ? 'grey.50' : 'transparent',
              }}
            >
              <Typography fontSize={14} flex={1}>
                {x.license}
              </Typography>
              <Typography fontSize={14} flex={1}>
                {x.firstName} {x.lastName}
              </Typography>
              <Typography fontSize={14} flex={1} textAlign="right">
                ${x.amount}
              </Typography>
            </Stack>
          ))}
        </ContentSection>
        <iframe
          style={{ paddingTop: 1, border: 'none' }}
          id="hpp"
          name="hpp"
          title="hpp"
          width="100%"
          height={hppSrc ? 1200 : 800}
          src={hppSrc}
        ></iframe>
        <form target="hpp" action={src} method="POST" ref={formRef}>
          <input type="hidden" name="token" value={state?.token ?? ''} />
        </form>
      </Stack>
    </PageWrapper>
  );
};
